// hooks/useTrackingLogic.js
import { useRef, useState, useEffect } from 'react';
import { useCustomFormik } from 'src/components/formik-helper';
import { handleTrackingSubmitted } from 'src/pages/students/formik';
import { mergeNoteFromEditor } from 'src/utils/formatters';
import { getCacheTrackings } from 'src/utils/cache-data';

export const useTrackingLogic = ({ item, apiCreate, apiUpdate, apiDelete, onNextStep }) => {
  const dropdownRef = useRef();
  const statusRef = useRef();
  const pickerRef = useRef();
  const trackingRef = useRef({ id: null, optionId: null });
  const editorRef = useRef();

  const [trackingList, setTrackingList] = useState(item.tracking || []);
  const [element, setElement] = useState({});
  const [options, setOptions] = useState([]);
  const [submitState, setSubmitState] = useState({ isRemoved: false, isDisabled: false, isVerified: false });
  const [unusedStatusIds, setUnusedStatusIds] = useState([]);
  const [viewSelectedOptionId, setViewSelectedOptionId] = useState(null);
  const [trackingOptions, setTrackingOptions] = useState([]);

  const formik = useCustomFormik({
    initialValues: { trackingField: null, statusField: null },
    onSubmit: async (values) => {
      values.studentId = item.id;
      
      const mergeValues = mergeNoteFromEditor(values, editorRef, item.note);
      await handleTrackingSubmitted(mergeValues, unusedStatusIds, apiCreate, apiUpdate, apiDelete);
    },
  });

  useEffect(() => {
    setTrackingList(item.tracking || []);
  }, [item.tracking]);

   useEffect(() => {
    const savedData = getCacheTrackings();
    setTrackingOptions(savedData);
  }, []);

  const resetFields = () => {
    dropdownRef.current?.reset?.();
    pickerRef.current?.reset?.();
    editorRef.current?.reset?.();

    formik.setFieldValue('myId', null);
    // formik.setFieldValue('myRepeated', null);
    formik.setFieldValue('trackingField', null);
    formik.setFieldValue('statusField', null);
    formik.setFieldValue('remindField', null);

    setOptions([]);
    setElement({});
    setSubmitState({ isRemoved: false, isDisabled: false, isVerified: false });
    trackingRef.current = { id: null, optionId: null };
    setUnusedStatusIds([]);
  };

  const handleTrackingSelect = (val) => {
    if (!val) {
      resetFields();
      return;
    }
    statusRef.current?.reset?.();
    setOptions(val.status || []);

    const mOptionId = val.value;
    formik.setFieldValue('trackingField', mOptionId);
    formik.setFieldValue('statusField', null);

    const { id: cachedId, optionId: cachedOptionId, fromItem } = trackingRef.current;
    trackingRef.current = { fromItem: false }

    const isNewSelection = mOptionId !== cachedOptionId;

    let match = isNewSelection
      ? trackingList.find(el => el.optionId === mOptionId)
      : trackingList.find(el => el.id === cachedId);

    const myId = match?.id ?? null;
    const trackingLabel = match?.trackingName ?? val.label;
    const trackingStatus = match?.statusName ?? null;
    const trackingNote = match?.note ?? null;

    if(!fromItem){
      // const shouldIncrementRepeated = [1, 2].includes(val.type);
      // if(shouldIncrementRepeated){
      //   const incrementedRepeated = (parseInt(match?.repeated ?? 0, 10) || 0) + 1;
      //   formik.setFieldValue('myRepeated', incrementedRepeated);
      // }

      setElement({});
      // setViewSelectedOptionId(null);
      setViewSelectedOptionId(mOptionId || null);

      const disabled = ![0, 1].includes(val.type) && !!mOptionId;
      setSubmitState(prev => ({
        ...prev,
        isRemoved: false,
        isDisabled: disabled,
        isVerified: false
      }));

      setUnusedStatusIds([]);
      return;
    }

    formik.setFieldValue('myId', myId);
    setElement({ tracking: trackingLabel, status: trackingStatus, note: trackingNote });
    setViewSelectedOptionId(match?.optionId ?? null);

    setSubmitState(prev => ({
      ...prev,
      isRemoved: !!match,
      isDisabled: !!mOptionId,
      isVerified: false
    }));

    const currentStatusId = match?.statusId ?? null;
    const unusedIds = currentStatusId > 0 ? getUnusedStatusIds(val, trackingList) : [];
    setUnusedStatusIds(unusedIds);

    const objTemp = val.status.find(s => s.value === match?.statusId);
    sendNextStep(objTemp?.nextTracking, objTemp?.nextStatus);
  };

  const handleStatusSelect = (val) => {
    formik.setFieldValue('statusField', val?.value);
    const isRemoved = val?.label === element.status;
    const trackingItem = trackingList.find(
      el => el.optionId === viewSelectedOptionId && el.statusId === val?.value
    );

    if (trackingItem) {
      setElement({
        tracking: trackingItem.trackingName,
        status: trackingItem.statusName,
        note: trackingItem.note,
      });

      editorRef.current?.setValue?.(trackingItem.note || '');
    } else {
      // Not found: reset note, status
      setElement({
        tracking: element.tracking,
        status: '',
        note: '',
      });

      editorRef.current?.setValue?.('');
    }

    setSubmitState(prev => ({
      ...prev,
      isRemoved,
      isVerified: false,
      isDisabled: !!val?.value,
    }));

    sendNextStep(val?.nextTracking, val?.nextStatus);
  };

  const sendNextStep = (trackingId, statusId) => {
    const tracking = trackingOptions.find(t => t.value === trackingId);
    const status = tracking?.status?.find(s => s.value === statusId);

    onNextStep({
      trackingLabel: tracking?.label || null,
      statusLabel: status?.label || null
    });
  };

  const getNextTrackingInfo = (statusValue) => {
 
    const status = tracking.status?.find(s => s.value === statusValue);
    if (status) {
      return {
        nextTracking: status.nextTracking,
        nextStatus: status.nextStatus
      };
    }
  };


  const getUnusedStatusIds = (statusObject, trackingList) => {
    if (!statusObject?.value || !Array.isArray(statusObject.status)) return [];
  
    // Step 1: find all used statusIds for the matching optionId
    const usedStatusIds = trackingList
      .filter(t => t.optionId === statusObject.value)
      .map(t => t.statusId);
  
    // Step 2: return only status.value not used
    const unusedStatus = statusObject.status.filter(opt => !usedStatusIds.includes(opt.value));
    return unusedStatus.map(opt => opt.value);
  };
  
  const handleItemClicked = (item) => {
    // Cache myId
    trackingRef.current = { id: item.id, optionId: item.optionId, fromItem: true };
    // Auto call DropdownTracking for choose by id
    dropdownRef.current?.handleSelect(item.optionId);
  };

  return {
    formik,
    trackingList,
    element,
    options,
    dropdownRef,
    statusRef,
    pickerRef,
    editorRef,
    handleTrackingSelect,
    handleItemClicked,
    handleStatusSelect,
    viewSelectedOptionId,
    submitState,
    setSubmitState,
    resetFields,
    setTrackingList,
  };
};
